import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, throwError, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Preferences } from '@capacitor/preferences';
import { environment } from '../../environments/environment';
import { Account } from '../models/account';
import { User } from '../models/user';
import { ApiService } from './api.service';
import { Intercom } from '@capacitor-community/intercom';
import { Intercom as IntercomWeb } from '@intercom/messenger-js-sdk';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { Platform } from '@ionic/angular';
import { LocalNotificationService } from './local-notification.service';
import { Router } from '@angular/router';
import { SubscriptionService } from '../subscription.service';
import * as Sentry from "@sentry/capacitor";
import { FacebookLogin } from '@capacitor-community/facebook-login';

@Injectable()
export class AuthService {
  base_path = environment.API_URL;

  // Logged in Observable
  private loggedInSubject = new BehaviorSubject<boolean>(false);
  loggedIn$ = this.loggedInSubject.asObservable();

  // Account selection Observable
  private selectedAccountSubject = new BehaviorSubject<string | null>(null);
  selectedAccount$ = this.selectedAccountSubject.asObservable();

  // IntercomWeb
  intercomWeb: any;
  // Guard to avoid duplicate initialisation
  private intercomInitialized = false;
  // Track if we must collect user details before proceeding
  private pendingUserSetup = false;

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private platform: Platform,
    private localNotificationService: LocalNotificationService,
    private router: Router,
    private subscriptionService: SubscriptionService) {
    this.initialize();
  }

  private async initialize() {
    const isLoggedIn = await this.isLoggedIn();
    this.loggedInSubject.next(isLoggedIn);

    // If we have a session, try to initialise Intercom ASAP using cached values
    if (isLoggedIn) {
      await this.initializeIntercomFromCache();
    }
  }

  login(email: string, password: string): Observable<any> {
    return this.apiService.login(email, password).pipe(
      switchMap(tokenResponse => this.handleAuthentication(tokenResponse)),
      catchError(error => {
        console.error('Login error:', error);
        return throwError(() => new Error('Login has Failed. Please try again.'));
      })
    );
  }

  createUserAccount(email: string, password: string, name: string): Observable<any> {
    return this.apiService.createUserAccount(email, password, name).pipe(
      switchMap(tokenResponse => this.handleAuthentication(tokenResponse)),
      catchError(error => {
        console.error('Auth Service: Create User Account error:', error);
        return throwError(() => new Error('Create User Account has Failed. Please try again.'));
      })
    );
  }

  createAccount(businessName: string, country?: string, currency?: string): Observable<Account> {
    return this.apiService.createAccount(businessName, country, currency).pipe(
      tap(newAccount => {
        // Assuming the response directly gives you the new account object
        // Convert the account ID to a string if necessary, depending on your application's needs
        this.setSelectedAccount(String(newAccount.id)).then(() => {
          console.log(`New account created and selected: ${newAccount.id}`);
          this.selectedAccountSubject.next(String(newAccount.id));
          // You might want to perform other actions here, like initializing services or fetching additional data
        });
      }),
      catchError(error => {
        console.error('Auth Service: Create Account error:', error);
        return throwError(() => new Error('Create Account has Failed. Please try again.'));
      })
    );
  }

  // Sentry setUser in here too
  private handleAuthentication(tokenResponse: any): Observable<any> {
    return from(this.saveTokens(tokenResponse)).pipe(
      switchMap(() => from(this.getUserDetails().toPromise())),
      tap(async user => await this.cacheIntercomUser(user)),
      tap(user => Sentry.setUser({ id: user.id, email: user.email, name: user.name })),
      tap(user => this.initializeIntercom(user)),
      tap(user => this.initializeSubscriptions(user)),
      tap(user => {
        // Route to user setup if name missing; otherwise continue
        this.pendingUserSetup = !user?.name;
        if (this.pendingUserSetup) {
          this.router.navigateByUrl('/user-setup');
        }
      }),
      switchMap(() => this.pendingUserSetup ? of([]) : this.getCurrentAccounts()),
      switchMap(accounts => {
        if (this.pendingUserSetup) {
          return of(null);
        }
        if (accounts && accounts.length > 0) {
          return from(this.setSelectedAccount(String(accounts[0].id)));
        } else {
          console.log('No accounts found for the user.');
          this.router.navigateByUrl('/account-wizard');
          return of(null);
        }
      }),
      tap(() => this.loggedInSubject.next(true)), // Indicate successful login after setup
      // Log CompleteRegistration event without await by using from()
      switchMap(() => from(
          FacebookLogin.logEvent({
              eventName: 'CompleteRegistration'
          })
      )),
      catchError(error => {
        console.error('Error during post-login setup:', error);
        return throwError(() => new Error('Post-login setup failed.'));
      })
    );
  }

  private async saveTokens(tokenResponse: any): Promise<void> {
    await Preferences.set({ key: 'accessToken', value: tokenResponse.accessToken });
    await Preferences.set({ key: 'refreshToken', value: tokenResponse.refreshToken });
    console.log('Tokens saved');
  }

  initializePlatformFeatures(user: User) {
    this.platform.ready().then(() => {
      console.log('Platform Ready');
      
      if (Capacitor.isNativePlatform()) {
        this.localNotificationService.refreshNotifications();
        
        const isPushNotificationsAvailable = Capacitor.isPluginAvailable('PushNotifications');
        if (isPushNotificationsAvailable) {
          PushNotifications.register();
        }

      }

      // Intercom
      this.initializeIntercom(user);

      // Subscriptions / RevenueCat - caters for all platforms
      this.initializeSubscriptions(user);
    });
  }

  private initializeIntercom(user: User) {
    console.log('Initializing Intercom');
    console.log('User:', user);
    if (!user || !user.id || !user.email) {
      console.warn('Intercom init skipped: missing user id or email');
      return;
    }
    const userUpdate = {
      email: user.email,
      name: user.name,
      userId: user.id.toString(),
      user_hash: user.intercomHash,
      companies: [] // Assuming this needs to be populated based on the user's accounts
    };
    
    if (Capacitor.isNativePlatform()) {
      Intercom.registerIdentifiedUser({ 
        userId: user.id.toString() ,
        email: user.email
      });
      Intercom.setUserHash({ hmac: user.intercomHash });
      Intercom.updateUser(userUpdate);
      this.intercomInitialized = true;
    }
    else {
      // Ensure a clean boot with the correct user_id
      if (this.intercomWeb) {
        try { this.intercomWeb.shutdown && this.intercomWeb.shutdown(); } catch (e) { console.warn('Intercom web shutdown error', e); }
        this.intercomWeb = undefined;
      }
      this.intercomWeb = IntercomWeb({
        app_id: environment.INTERCOM_IOS_APP_ID,
        email: user.email,
        name: user.name,
        user_id: user.id.toString(),
        user_hash: user.intercomHash,
        custom_launcher_selector: '#custom-messenger', // Optional custom launcher selector
        hide_default_launcher: true,
      })
      this.intercomInitialized = true;
    }
  }

  private async cacheIntercomUser(user: User): Promise<void> {
    try {
      await Preferences.set({ key: 'intercom_user_id', value: user?.id?.toString?.() || '' });
      await Preferences.set({ key: 'intercom_user_email', value: user?.email || '' });
      await Preferences.set({ key: 'intercom_user_name', value: user?.name || '' });
      await Preferences.set({ key: 'intercom_user_hash', value: user?.intercomHash || '' });
      console.log('Cached Intercom user');
    } catch (e) {
      console.warn('Failed to cache Intercom user', e);
    }
  }

  private async initializeIntercomFromCache(): Promise<void> {
    try {
      const [{ value: id }, { value: email }, { value: name }, { value: hash }] = await Promise.all([
        Preferences.get({ key: 'intercom_user_id' }),
        Preferences.get({ key: 'intercom_user_email' }),
        Preferences.get({ key: 'intercom_user_name' }),
        Preferences.get({ key: 'intercom_user_hash' })
      ]);

      if (this.intercomInitialized) {
        return;
      }

      if (id && email) {
        console.log('Initializing Intercom from cache');
        this.initializeIntercom({
          id: Number(id),
          email: email,
          name: name,
          intercomHash: hash
        } as unknown as User);
      }
    } catch (e) {
      console.warn('Failed to initialize Intercom from cache', e);
    }
  }

  private initializeSubscriptions(user: User) {
    console.log('AuthService: Initializing Subscriptions Service for user:', user);
    this.subscriptionService.init(user);
  }

  async isLoggedIn(): Promise<boolean> {
    const accessToken = await Preferences.get({ key: 'accessToken' }).then(({ value }) => value);
    return !!accessToken;
  }

  getUserDetails(): Observable<User> {
    console.log('Auth Service: Fetching user details');
    const headers = new HttpHeaders().set('X-Skip-Account-Interceptor', 'true');
    return this.http.get<User>(`${this.base_path}auth/current`, { headers }).pipe(
        tap(user => {
            // Check the active status and store it
            if (!user.active) {
                this.router.navigate(['/paywall']); // Redirect to paywall if not active
            }
        })
    );
}


  logout() {
    this.clearTokens().then(() => {
      if (Capacitor.isNativePlatform()) {
        Intercom.logout();
        Intercom.registerUnidentifiedUser();
      }
      else {
        if (this.intercomWeb && this.intercomWeb.shutdown) {
          this.intercomWeb.shutdown();
        }
        this.intercomWeb = undefined;
      }
    });
  }

  private async clearTokens(): Promise<void> {
    await Preferences.remove({ key: 'accessToken' });
    await Preferences.remove({ key: 'refreshToken' });
    await Preferences.remove({ key: 'selectedAccount' });
    await Preferences.remove({ key: 'intercom_user_id' });
    await Preferences.remove({ key: 'intercom_user_email' });
    await Preferences.remove({ key: 'intercom_user_name' });
    await Preferences.remove({ key: 'intercom_user_hash' });
    this.loggedInSubject.next(false);
    this.intercomInitialized = false;
  }

  getAccessToken(): Promise<string | null> {
    // Retrieve the access token from the storage or any other source
    return Preferences.get({ key: 'accessToken' }).then(({ value }) => value);
  }

  // Method to refresh the access token using the refresh token
  refreshAccessToken(): Observable<string> {
    return from(Preferences.get({ key: 'refreshToken' })).pipe(
      switchMap(({ value: refreshToken }) => {
        const requestBody = {
          refreshToken: refreshToken,
        };

        const headers = new HttpHeaders().set('X-Skip-Account-Interceptor', 'true');

        return this.http.post<any>(this.base_path + 'auth/refresh', requestBody, { headers }).pipe(
          map(response => {
            Preferences.set({
              key: 'accessToken',
              value: response.accessToken,
            }).then(() => {
              console.log('Access token updated');
            });

            return response.accessToken;
          })
        );
      })
    );
  }

  // Change password by sending a request to the API
  changePassword(currentPassword: string, newPassword: string): Observable<any> {
    return this.apiService.changePassword(currentPassword, newPassword);
  }

  /*
  * Accounts
  */

  // Get a list of current accounts ensuring that the interceptor is skipped
  getCurrentAccounts(): Observable<Account[]> {
    console.log('Fetching accounts for the user');
    const headers = new HttpHeaders().set('X-Skip-Account-Interceptor', 'true');
    // Update the URL as necessary to point to your backend endpoint for fetching accounts
    return this.http.get<Account[]>(`${this.base_path}accounts`, { headers }).pipe(
      catchError(error => {
        console.error('Error fetching accounts:', error);
        return throwError(() => new Error('Failed to fetch accounts.'));
      })
    );
  }

  async getSelectedAccountId(): Promise<string | null> {
    let selectedAccount = await Preferences.get({ key: 'selectedAccount' }).then(({ value }) => value);
    console.log('getSelectedAccountId:', selectedAccount);
  
    // Check if a selected account exists
    if (!selectedAccount) {
      // No selected account, set a default one
      //await this.setDefaultAccount();
  
      // After setting the default account, fetch the selected account ID again
      selectedAccount = await Preferences.get({ key: 'selectedAccount' }).then(({ value }) => value);
    }
  
    return selectedAccount;
  }

  // Method to reset the currently selected account ID in local storage
  resetAccount() {
    return Preferences.remove({ key: 'selectedAccount' });
  }

  async setSelectedAccount(accountId: string): Promise<void> {
    try {
      // Validate accountId
      if (!accountId) {
        console.error('setSelectedAccount: Invalid accountId:', accountId);
        return;
      }
  
      // Log the incoming accountId
      console.log('setSelectedAccount:', accountId);
  
      // Save the selected account ID in preferences
      await Preferences.set({ key: 'selectedAccount', value: ""+accountId });
  
      // Notify subscribers
      this.selectedAccountSubject.next(accountId);
      console.log(`setSelectedAccount: Account ${accountId} set as selected.`);
  
      // Check and log the saved value
      const { value } = await Preferences.get({ key: 'selectedAccount' });
      if (value) {
        console.log('setSelectedAccount: [test] Selected Account:', value);
      } else {
        console.error('setSelectedAccount: No value found for selectedAccount');
      }
    } catch (error) {
      console.error('setSelectedAccount: ssError in setSelectedAccount:', error);
    }
  }
  

  async getSelectedAccount(): Promise<Account | null> {
    const selectedAccountId = await this.getSelectedAccountId();

    if (selectedAccountId) {
      // Fetch account from the API based on the selected account ID
      return this.http.get<Account>(`${this.base_path}accounts/${selectedAccountId}`).toPromise();
    }

    return null; // No selected account
  }

  async setDefaultAccount(): Promise<boolean> {
    const headers = new HttpHeaders().set('X-Skip-Account-Interceptor', 'true');
    // Get a list of accounts and set selectedAccount to the first one
    let accounts = await this.http.get<Account[]>(this.base_path + 'accounts', { headers }).toPromise();

    // Ensure accounts is always an array
    accounts = accounts || [];
  
    try {    
      if (accounts.length > 0) {
        const defaultAccount = accounts[0]; // Choose the first account as the default
        await this.setSelectedAccount(""+defaultAccount.id); // Set the default account as selected
        return true; // Return true if a default account was set
      }
      else {
        console.log('No accounts found');
        return false; // Return false if no accounts were found
      }
    } catch (error) {
      console.error('Error setting default account:', error);
      return false; // Return false if an error occurred
    }
  }

  // Delete the Account
  deleteAccount(accountId: string): Observable<any> {
    return this.apiService.deleteAccount(""+accountId);
  }

  // Delete the user and all accounts (just delete user using API)
  deleteUser(): Observable<any> {
    return this.apiService.deleteUser();
  }
}
